import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  IconButton,
  Box,
} from "@mui/material";
import {
  Dashboard,
  Settings,
  AdminPanelSettings,
  Assessment,
  Store,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  BuildCircle,
  Visibility,
  History,
  Group,
  Person
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

const Sidebar = ({ isAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [storeMatrixOpen, setStoreMatrixOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const toggleStoreMatrix = () => setStoreMatrixOpen((prev) => !prev);
  const toggleSettings = () => setSettingsOpen((prev) => !prev);
  const toggleAdminMenu = () => setAdminOpen((prev) => !prev);
  const toggleSidebar = () => setCollapsed((prev) => !prev);

  const isActive = (path) => location.pathname === path;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: collapsed ? 60 : 240,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: collapsed ? 60 : 240,
          boxSizing: "border-box",
          marginTop: "64px",
          overflowX: "hidden",
          transition: "width 0.3s ease",
        },
      }}
    >
      <Box display="flex" justifyContent="flex-end" p={1}>
        <IconButton onClick={toggleSidebar}>
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
      <List>
        {/* Dashboard */}
        <ListItem
          onClick={() => navigate("/")}
          selected={isActive("/")}
          sx={{
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
            "&.Mui-selected": {
              backgroundColor: "#a6d7de",
              "&:hover": { backgroundColor: "#add8e6" },
            },
          }}
        >
          <ListItemIcon>
            <Dashboard />
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Dashboard" />}
        </ListItem>

        {/* Store Matrix Section */}
        <ListItem
          onClick={toggleStoreMatrix}
          selected={
            storeMatrixOpen ||
            [
              "/build-store-matrix",
              "/view-store-matrix",
              "/view-audit-logs",
              "/import-store-matrix",
            ].includes(location.pathname)
          }
          sx={{
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
            "&.Mui-selected": {
              backgroundColor: "#a6d7de",
              "&:hover": { backgroundColor: "#add8e6" },
            },
          }}
        >
          <ListItemIcon>
            <Store />
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Store Matrix" />}
          {storeMatrixOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse
          in={storeMatrixOpen || collapsed}
          timeout="auto"
          unmountOnExit
        >
          <List component="div" disablePadding>
            <ListItem
              onClick={() => navigate("/build-store-matrix")}
              selected={isActive("/build-store-matrix")}
              sx={{
                cursor: "pointer",
                pl: collapsed ? 2 : 4,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-selected": {
                  backgroundColor: "#a6d7de",
                  "&:hover": { backgroundColor: "#add8e6" },
                },
              }}
            >
              <ListItemIcon>
                <BuildCircle />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="Build Store Matrix" />}
            </ListItem>
            <ListItem
              onClick={() => navigate("/view-store-matrix")}
              selected={isActive("/view-store-matrix")}
              sx={{
                cursor: "pointer",
                pl: collapsed ? 2 : 4,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-selected": {
                  backgroundColor: "#a6d7de",
                  "&:hover": { backgroundColor: "#add8e6" },
                },
              }}
            >
              <ListItemIcon>
                <Visibility />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="View Store Matrix" />}
            </ListItem>
            <ListItem
              onClick={() => navigate("/view-audit-logs")}
              selected={isActive("/view-audit-logs")}
              sx={{
                cursor: "pointer",
                pl: collapsed ? 2 : 4,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-selected": {
                  backgroundColor: "#a6d7de",
                  "&:hover": { backgroundColor: "#add8e6" },
                },
              }}
            >
              <ListItemIcon>
                <History />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="View Audit Logs" />}
            </ListItem>
            <ListItem
              onClick={() => navigate("/import-store-matrix")}
              selected={isActive("/import-store-matrix")}
              sx={{
                cursor: "pointer",
                pl: collapsed ? 2 : 4,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-selected": {
                  backgroundColor: "#a6d7de",
                  "&:hover": { backgroundColor: "#add8e6" },
                },
              }}
            >
              <ListItemIcon>
                <Store />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="Import Store Matrix" />}
            </ListItem>
          </List>
        </Collapse>

        {/* Other sections */}
        <ListItem
          onClick={() => navigate("/reports")}
          selected={isActive("/reports")}
          sx={{
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
            "&.Mui-selected": {
              backgroundColor: "#a6d7de",
              "&:hover": { backgroundColor: "#add8e6" },
            },
          }}
        >
          <ListItemIcon>
            <Assessment />
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Reports" />}
        </ListItem>

        {/* Admin Panel */}
        {isAdmin && (
          <>
            <ListItem
              onClick={toggleSettings}
              selected={
                settingsOpen || ["/manage-groups"].includes(location.pathname)
              }
              sx={{
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-selected": {
                  backgroundColor: "#a6d7de",
                  "&:hover": { backgroundColor: "#add8e6" },
                },
              }}
            >
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="Settings" />}
              {settingsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse
              in={settingsOpen || collapsed}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <ListItem
                  onClick={() => navigate("/manage-groups")}
                  selected={isActive("/manage-groups")}
                  sx={{
                    cursor: "pointer",
                    pl: collapsed ? 2 : 4,
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#a6d7de",
                      "&:hover": { backgroundColor: "#add8e6" },
                    },
                  }}
                >
                  <ListItemIcon>
                    <Group />
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary="Manage Groups" />}
                </ListItem>
              </List>
            </Collapse>
            <ListItem
              onClick={toggleAdminMenu}
              selected={adminOpen || location.pathname.startsWith("/admin")}
              sx={{
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-selected": {
                  backgroundColor: "#a6d7de",
                  "&:hover": { backgroundColor: "#add8e6" },
                },
              }}
            >
              <ListItemIcon>
                <AdminPanelSettings />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="Admin Panel" />}
              {adminOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={adminOpen || collapsed} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem
                  onClick={() => navigate("/admin/manage-users")}
                  selected={isActive("/admin/manage-users")}
                  sx={{
                    cursor: "pointer",
                    pl: collapsed ? 2 : 4,
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#a6d7de",
                      "&:hover": { backgroundColor: "#add8e6" },
                    },
                  }}
                >
                  <ListItemIcon>
                    <Person />
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary="Manage Users" />}
                </ListItem>
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Drawer>
  );
};

export default Sidebar;
